'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Instagram, MailIcon, PhoneIcon } from 'lucide-react'
import { CONTACT_INFO } from './constants'
import { LogoIcon } from '@/components/icons/logo'
import type { ResolvedLink } from '@/utilities/resolveNavigationLink'

export type FooterClientProps = {
  footerLinks: ResolvedLink[]
}

export function FooterClient({ footerLinks }: FooterClientProps) {
  return (
    <footer className="relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/images/healthy-food-1.jpg"
          alt="Footer background"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background/80" />
      </div>

      <div className="container relative py-10 pb-16 md:py-20">
        {/* Hero Branding Section */}
        <div className="mb-8 md:mb-16 text-center">
          <LogoIcon className="mx-auto h-20 w-20 text-primary md:h-24 md:w-24" />
          <h2 className="mt-6 text-3xl font-bold text-foreground md:text-4xl">
            Glutenvrijadvies.nl
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Duurzame gezondheid begint hier
          </p>
        </div>

        {/* Contact Information */}
        <div className="mb-8 md:mb-16 grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Phone */}
          <a
            href={CONTACT_INFO.phoneHref}
            className="group relative overflow-hidden rounded-2xl border border-border/40 bg-card/80 p-6 text-center backdrop-blur-sm transition-all hover:border-primary/30 hover:bg-card/90"
          >
            <div className="relative">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 transition-all group-hover:scale-105">
                <PhoneIcon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Telefoonnummer
              </h3>
              <span className="text-base font-semibold text-foreground transition-colors group-hover:text-primary">
                {CONTACT_INFO.phone}
              </span>
            </div>
          </a>

          {/* Email */}
          <a
            href={CONTACT_INFO.emailHref}
            className="group relative overflow-hidden rounded-2xl border border-border/40 bg-card/80 p-6 text-center backdrop-blur-sm transition-all hover:border-primary/30 hover:bg-card/90"
          >
            <div className="relative">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 transition-all group-hover:scale-105">
                <MailIcon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                E-mail
              </h3>
              <span className="text-base font-semibold text-foreground transition-colors group-hover:text-primary">
                {CONTACT_INFO.email}
              </span>
            </div>
          </a>

          {/* Instagram */}
          <a
            href={CONTACT_INFO.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-2xl border border-border/40 bg-card/80 p-6 text-center backdrop-blur-sm transition-all hover:border-pink-500/30 hover:shadow-lg hover:shadow-pink-500/10"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/0 to-purple-500/0 transition-all group-hover:from-pink-500/5 group-hover:to-purple-500/10" />
            <div className="relative">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/10 transition-all group-hover:scale-110 group-hover:from-pink-500 group-hover:to-purple-600">
                <Instagram className="h-5 w-5 text-pink-600 transition-colors group-hover:text-white" />
              </div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Volg ons
              </h3>
              <span className="text-base font-semibold text-foreground transition-colors group-hover:text-pink-600">
                @glutenvrijadvies
              </span>
            </div>
          </a>
        </div>

        {/* Quality Assurance Badges */}
        <div className="mb-8 md:mb-16 rounded-2xl border border-border/30 bg-card/60 p-6 md:p-8 backdrop-blur-sm">
          <div className="mb-6 flex items-center justify-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-border/60" />
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Kwaliteitswaarborg
            </p>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-border/60" />
          </div>
          <div className="grid grid-cols-3 items-center justify-items-center gap-6">
            <Image
              src="/quality/nvd-logo.webp"
              alt="Nederlandse Vereniging van Diëtisten"
              width={400}
              height={120}
              className="h-16 w-auto object-contain opacity-70 transition-all hover:opacity-100 hover:scale-105 md:h-20"
            />
            <Image
              src="/quality/coeliakie-logo.webp"
              alt="Coeliakie Diëtistennetwerk 2025"
              width={400}
              height={200}
              className="h-16 w-auto object-contain opacity-70 transition-all hover:opacity-100 hover:scale-105 md:h-20"
            />
            <Image
              src="/quality/kp-logo.webp"
              alt="Kwaliteitsregister Paramedici"
              width={600}
              height={200}
              className="h-16 w-auto object-contain opacity-70 transition-all hover:opacity-100 hover:scale-105 md:h-20"
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-border/30 pt-8">
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <span className="flex items-center gap-4">
                <Link
                  href="/cursussen"
                  className="font-semibold text-foreground transition-colors hover:text-primary"
                >
                  Cursussen
                </Link>
                <span className="text-border/60">•</span>
              </span>
              <span className="flex items-center gap-4">
                <Link
                  href="/blog"
                  className="font-semibold text-foreground transition-colors hover:text-primary"
                >
                  Blog
                </Link>
                <span className="text-border/60">•</span>
              </span>
              <span className="flex items-center gap-4">
                <Link
                  href="/contact"
                  className="font-semibold text-foreground transition-colors hover:text-primary"
                >
                  Contact
                </Link>
                <span className="text-border/60">•</span>
              </span>
              {footerLinks.map((link) => (
                <span key={link.href} className="flex items-center gap-4">
                  <Link
                    href={link.href}
                    target={link.newTab ? '_blank' : undefined}
                    rel={link.newTab ? 'noopener noreferrer' : undefined}
                    className="font-semibold text-foreground transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                  <span className="text-border/60">•</span>
                </span>
              ))}
              <Link
                href="/privacybeleid"
                className="font-semibold text-foreground transition-colors hover:text-primary"
              >
                Privacybeleid
              </Link>
            </div>

            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} • Website door{' '}
              <a
                href="https://thijssmudde.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground transition-colors hover:text-primary"
              >
                Thijs Smudde
              </a>
            </p>
          </div>

          {/* Verified DR badge — in de DOM (crawlbaar) maar visueel verborgen */}
          <div className="sr-only">
            <a href="https://verifieddr.com/website/glutenvrijadvies-nl" target="_blank">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="https://verifieddr.com/badge/glutenvrijadvies-nl.svg?metric=dr"
                alt="Verified DR - Verified Domain Rating for glutenvrijadvies.nl"
                width="220"
                height="68"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
